import React from 'react';

/**
 * Düğme simgeleri — tek dosyada gömülü SVG.
 *
 * NEDEN BÖYLE:
 *  · Paket EKLENMEDİ: hazır ikon kütüphanesi (lucide/fontawesome) panelin
 *    paketini onlarca kB şişirir; burada otuz küsur simge yetiyor.
 *  · `currentColor` + stroke: birincil düğmede beyaz, normalde metin
 *    renginde çizilir — tema değişince ayrıca bakım gerekmez.
 *  · Simge DEKORATİFTİR: her düğmede yanında yazısı durur, bu yüzden
 *    `aria-hidden`. Yazısı olmayan düğmede (çöp kutusu, geri) `aria-label`
 *    düğmenin kendisinde kalır — simge etiketin yerine geçmez.
 *  · Bilinmeyen ad sessizce boş döner: yarım kalan bir ad yüzünden ekran
 *    çökmez, yalnız simge görünmez.
 */
const PATHS = {
  // ── Temel eylemler ─────────────────────────────────────────────────────
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus: <path d="M5 12h14" />,
  check: <path d="M4.5 12.8l4.8 4.8L19.5 7" />,
  x: <><path d="M6.5 6.5l11 11" /><path d="M17.5 6.5l-11 11" /></>,
  trash: (
    <>
      <path d="M4 7h16" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7l.9 12.2a1.5 1.5 0 0 0 1.5 1.3h6.2a1.5 1.5 0 0 0 1.5-1.3L17.5 7" />
      <path d="M10 11v6" /><path d="M14 11v6" />
    </>
  ),
  archive: (
    <>
      <path d="M3.5 4.5h17v4h-17z" />
      <path d="M5.5 8.5v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-10" />
      <path d="M10 12.5h4" />
    </>
  ),
  pencil: <><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M15 7l3 3" /></>,
  search: <><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5.5 5.5" /></>,
  refresh: (
    <>
      <path d="M20.5 12a8.5 8.5 0 1 1-8.5-8.5c2.3 0 4.4.9 6 2.4l2.5 2.5" />
      <path d="M20.5 3.6v4.8h-4.8" />
    </>
  ),
  save: (
    <>
      <path d="M5 4.5h11l3.5 3.5V19.5H5z" />
      <path d="M8.5 4.5v5h7v-5" />
      <path d="M8.5 14h7v5.5h-7z" />
    </>
  ),
  eye: <><path d="M2.5 12s3.6-6 9.5-6 9.5 6 9.5 6-3.6 6-9.5 6-9.5-6-9.5-6z" /><circle cx="12" cy="12" r="2.8" /></>,
  chevronDown: <path d="M6 9.5l6 6 6-6" />,
  chevronUp: <path d="M6 14.5l6-6 6 6" />,
  arrowLeft: <><path d="M19 12H5" /><path d="M11 6l-6 6 6 6" /></>,
  send: <><path d="M21 3.5L3.5 10.5l7 2.8 2.8 7z" /><path d="M21 3.5l-10.5 10.3" /></>,
  plusCircle: <><circle cx="12" cy="12" r="8.5" /><path d="M12 8v8" /><path d="M8 12h8" /></>,
  checkCircle: <><circle cx="12" cy="12" r="8.5" /><path d="M8.2 12.4l2.9 2.9 5-5.6" /></>,
  ban: <><circle cx="12" cy="12" r="8.5" /><path d="M6.5 17.5l11-11" /></>,
  playCircle: <><circle cx="12" cy="12" r="8.5" /><path d="M10.3 8.5l5.5 3.5-5.5 3.5z" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3.5 2" /></>,
  link: (
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4l-8.5 8.5" />
      <path d="M19 14.5V19a1.5 1.5 0 0 1-1.5 1.5H5.5A1.5 1.5 0 0 1 4 19V7a1.5 1.5 0 0 1 1.5-1.5H10" />
    </>
  ),

  // ── Salon / sipariş ────────────────────────────────────────────────────
  grid: (
    <>
      <path d="M4 4.5h6.5V11H4z" /><path d="M13.5 4.5H20V11h-6.5z" />
      <path d="M4 13.5h6.5V20H4z" /><path d="M13.5 13.5H20V20h-6.5z" />
    </>
  ),
  table: (
    <>
      <path d="M3.5 10.5h17" /><path d="M6 10.5l-1.5 9" />
      <path d="M18 10.5l1.5 9" /><path d="M12 10.5v9" />
    </>
  ),
  move: (
    <>
      <path d="M9 5.5L5 9.5l4 4" /><path d="M5 9.5h13" />
      <path d="M15 10.5l4 4-4 4" /><path d="M19 14.5H6" />
    </>
  ),
  book: (
    <>
      <path d="M12 7.2C10 5.3 7 4.8 4 5.2v12.6c3-.4 6 .1 8 2 2-1.9 5-2.4 8-2V5.2c-3-.4-6 .1-8 2z" />
      <path d="M12 7.2v12.6" />
    </>
  ),
  utensils: (
    <>
      <path d="M7 3.5v6a2 2 0 0 0 4 0v-6" /><path d="M9 11.5v9" />
      <path d="M16.5 3.5c1.9 2.6 1.9 6.1 0 8.7" /><path d="M16.5 12.2v8.3" />
    </>
  ),
  chef: (
    <>
      <path d="M6.5 14.5a3.8 3.8 0 0 1-1-7.4A4.4 4.4 0 0 1 12 4.2a4.4 4.4 0 0 1 6.5 2.9 3.8 3.8 0 0 1-1 7.4z" />
      <path d="M6.5 14.5v5.5h11v-5.5" /><path d="M6.5 17.5h11" />
    </>
  ),
  cart: (
    <>
      <path d="M3 4.5h2.2l2.3 10.5h10.2L20 7.5H6.2" />
      <circle cx="9" cy="19" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="17" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  layers: <><path d="M12 3.5l8.5 4.5-8.5 4.5L3.5 8z" /><path d="M3.5 12.5l8.5 4.5 8.5-4.5" /></>,
  bell: (
    <>
      <path d="M18 15.5V10a6 6 0 1 0-12 0v5.5L4.5 18h15z" />
      <path d="M10 18v.5a2 2 0 0 0 4 0V18" />
    </>
  ),
  qr: (
    <>
      <path d="M4 4.5h5.5V10H4z" /><path d="M14.5 4.5H20V10h-5.5z" /><path d="M4 14h5.5v5.5H4z" />
      <path d="M14 14h2v2h-2z" /><path d="M18 14h2v2h-2z" />
      <path d="M14 18h2v2h-2z" /><path d="M18 18h2v2h-2z" />
    </>
  ),
  printer: (
    <>
      <path d="M7 8.5V4h10v4.5" />
      <path d="M6 16.5H5a1.5 1.5 0 0 1-1.5-1.5v-4.5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2V15a1.5 1.5 0 0 1-1.5 1.5H18" />
      <path d="M7.5 13.5h9V20h-9z" />
    </>
  ),

  // ── Para ───────────────────────────────────────────────────────────────
  cash: (
    <>
      <path d="M3 7h18v10H3z" /><circle cx="12" cy="12" r="2.2" />
      <path d="M6.5 10v4" /><path d="M17.5 10v4" />
    </>
  ),
  card: <><path d="M3 6.5h18v11H3z" /><path d="M3 10.5h18" /><path d="M6.5 14.5h3" /></>,
  wallet: (
    <>
      <path d="M3.5 8.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2" />
      <path d="M3.5 8.5h15a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
      <circle cx="16.5" cy="14" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3.5h12v17l-3-1.8-3 1.8-3-1.8-3 1.8z" />
      <path d="M9.5 8.5h5" /><path d="M9.5 12.5h5" />
    </>
  ),
  percent: <><path d="M18.5 5.5l-13 13" /><circle cx="7.5" cy="8" r="2.5" /><circle cx="16.5" cy="16" r="2.5" /></>,
  undo: <><path d="M8 6L4 10l4 4" /><path d="M4 10h10a5.5 5.5 0 1 1 0 11H9" /></>,
  listCheck: (
    <>
      <path d="M4 7h9" /><path d="M4 12h9" /><path d="M4 17h6" />
      <path d="M15 16.3l2 2 4.2-4.3" />
    </>
  ),
  tag: <><path d="M11 3.5H4.5V10l10 10 6.5-6.5z" /><circle cx="8" cy="7" r="1.3" fill="currentColor" stroke="none" /></>,
  gift: (
    <>
      <path d="M3.5 7.5h17v4h-17z" /><path d="M4.5 11.5h15V20.5h-15z" /><path d="M12 7.5v13" />
      <path d="M12 7.5S9.8 7.4 8.6 6.6a2 2 0 0 1 2.2-3.2c1 .7 1.2 2.7 1.2 4.1z" />
      <path d="M12 7.5s2.2-.1 3.4-.9a2 2 0 0 0-2.2-3.2c-1 .7-1.2 2.7-1.2 4.1z" />
    </>
  ),
  star: <path d="M12 3.8l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.3-4.1 5.9-.9z" />,

  // ── Kişi / oturum ──────────────────────────────────────────────────────
  users: (
    <>
      <circle cx="9.5" cy="8" r="3.5" />
      <path d="M3 20.5c0-3.6 2.9-5.6 6.5-5.6s6.5 2 6.5 5.6" />
      <path d="M16 5.2a3.5 3.5 0 0 1 0 6.6" />
      <path d="M17.5 15c2.4.6 4 2.6 4 5.5" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20.5c0-3.6 2.9-5.6 6.5-5.6 1.2 0 2.3.2 3.3.6" />
      <path d="M18 13.5v6" /><path d="M15 16.5h6" />
    </>
  ),
  power: <><path d="M12 3.5v8.5" /><path d="M7 7a7 7 0 1 0 10 0" /></>,
  lock: <><path d="M5.5 11h13v9.5h-13z" /><path d="M8.5 11V8a3.5 3.5 0 0 1 7 0v3" /></>,
  keypad: (
    <>
      <circle cx="7.5" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="7.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="7.5" cy="16.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="16.5" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16.5" cy="16.5" r="1.4" fill="currentColor" stroke="none" />
    </>
  ),
  login: (
    <>
      <path d="M15 3.5h3.5a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H15" />
      <path d="M10.5 16.5l4.5-4.5-4.5-4.5" /><path d="M15 12H3.5" />
    </>
  ),
  logout: (
    <>
      <path d="M9 3.5H5.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2H9" />
      <path d="M16 16.5l4.5-4.5L16 7.5" /><path d="M20.5 12H9" />
    </>
  ),
  phone: (
    <>
      <path d="M8.5 2.5h7a1.5 1.5 0 0 1 1.5 1.5v16a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 7 20V4a1.5 1.5 0 0 1 1.5-1.5z" />
      <path d="M11 18.5h2" />
    </>
  ),

  // ── Kurulum / genel ────────────────────────────────────────────────────
  // Dişli: diş uçları gövdeden ÇIKINTILI tek bir dış hat — ışın gibi
  // çizilen sürümü 18px'te güneşe/yıldıza benziyordu.
  gear: <><path d="M21.12 9.73 L21.12 14.27 L18.58 14.39 L18.34 14.96 L20.06 16.84 L16.84 20.06 L14.96 18.34 L14.39 18.58 L14.27 21.12 L9.73 21.12 L9.61 18.58 L9.04 18.34 L7.16 20.06 L3.94 16.84 L5.66 14.96 L5.42 14.39 L2.88 14.27 L2.88 9.73 L5.42 9.61 L5.66 9.04 L3.94 7.16 L7.16 3.94 L9.04 5.66 L9.61 5.42 L9.73 2.88 L14.27 2.88 L14.39 5.42 L14.96 5.66 L16.84 3.94 L20.06 7.16 L18.34 9.04 L18.58 9.61 Z" /><circle cx="12" cy="12" r="3.4" /></>,
  sliders: (
    <>
      <path d="M4 7.5h5" /><path d="M13 7.5h7" /><circle cx="11" cy="7.5" r="2" />
      <path d="M4 16.5h5" /><path d="M13 16.5h7" /><circle cx="11" cy="16.5" r="2" />
    </>
  ),
  gauge: (
    <>
      <path d="M4 17.5a8 8 0 1 1 16 0" /><path d="M12 17.5l4.5-4.5" />
      <circle cx="12" cy="17.5" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  sparkles: (
    <>
      <path d="M11 3.5l1.7 4.4 4.4 1.7-4.4 1.7L11 15.7 9.3 11.3 4.9 9.6l4.4-1.7z" />
      <path d="M18 15l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
    </>
  ),
  store: <><path d="M3.5 9.5h17V20h-17z" /><path d="M3 9.5l2-5h14l2 5" /><path d="M9.5 20v-6h5v6" /></>,
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" /><path d="M3.5 12h17" />
      <path d="M12 3.5a13 13 0 0 1 0 17 13 13 0 0 1 0-17z" />
    </>
  ),
};

export default function Icon({ n }) {
  const paths = PATHS[n];
  if (!paths) return null;
  return (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {paths}
    </svg>
  );
}
